import { SensorLockerStatus } from '@frontend/device/types';
import { SlotClient } from '@frontend/slot/api';
import { Slot } from '@frontend/slot/types';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useSlotListener } from '../../../hooks/useSlotListener/useSlotListener';
import { ProcessOpenCloseSlotsProps } from './process-open-close-slots.component';

interface ViewProps {
    slots: Slot[] | null;
    openSlots: Slot[];
    errorSlots: Slot[];
    closedSlots: Slot[];
    otherSlots: Slot[];
    slotStatusMap: Map<string, SensorLockerStatus> | null;
    allSlotsClosed: boolean;
    showClosedSlots: boolean;
    toggleShowClosedSlots: () => void;
}

const packageSlotKey = (packages: ProcessOpenCloseSlotsProps['packages']): string =>
    (packages ?? [])
        .map((pkg) => pkg.slot_id)
        .filter((slotId): slotId is string => !!slotId)
        .sort()
        .join(',');

const useProcessOpenCloseSlots = (props: ProcessOpenCloseSlotsProps): ViewProps => {
    const [slotStatusMap, setSlotStatusMap] = useState<Map<string, SensorLockerStatus> | null>(null);
    const slotStatusMapRef = useRef<Map<string, SensorLockerStatus>>(new Map());
    const packagesRef = useRef(props.packages);
    packagesRef.current = props.packages;
    const [slots, setSlots] = useState<Slot[] | null>(null);
    const [showClosedSlots, setShowClosedSlots] = useState<boolean>(true);

    const toggleShowClosedSlots = () => {
        setShowClosedSlots((prev) => !prev);
    };

    // Key by SLOT ids only — same SLOTs with a new package list must not reset MQTT statuses.
    const packagesSlotKey = useMemo(() => packageSlotKey(props.packages), [props.packages]);

    useEffect(() => {
        const packages = packagesRef.current;
        const statusMap = new Map<string, SensorLockerStatus>();
        packages?.forEach((pkg) => {
            if (pkg.slot_id) {
                statusMap.set(pkg.slot_id, SensorLockerStatus.OPENING);
            }
        });
        setSlotStatusMap(statusMap);
        slotStatusMapRef.current = statusMap;
        setSlots(null);

        const slotIds = packages?.filter((pkg) => pkg.slot_id).map((pkg) => pkg.slot_id!) ?? [];
        if (slotIds.length === 0) {
            setSlots([]);
            return;
        }

        let cancelled = false;
        SlotClient.resolveSlots(slotIds).then((response) => {
            if (!cancelled) {
                setSlots(response.results);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [packagesSlotKey]);

    useSlotListener({
        id: 'PROCESS_OPEN_CLOSE_SLOTS',
        onPackageSlotNotClosed(pkg, data) {
            if (pkg.slot_id && slotStatusMapRef.current.has(pkg.slot_id)) {
                setSlotStatusMap((prevMap) => {
                    const newMap = new Map(prevMap);
                    newMap.set(pkg.slot_id, SensorLockerStatus.NOT_CLOSED);
                    return newMap;
                });
                slotStatusMapRef.current.set(pkg.slot_id, SensorLockerStatus.NOT_CLOSED);
            }
        },
        onPackageSlotNotOpened(pkg, data) {
            if (pkg.slot_id && slotStatusMapRef.current.has(pkg.slot_id)) {
                setSlotStatusMap((prevMap) => {
                    const newMap = new Map(prevMap);
                    newMap.set(pkg.slot_id, SensorLockerStatus.NOT_OPENED);
                    return newMap;
                });
                slotStatusMapRef.current.set(pkg.slot_id, SensorLockerStatus.NOT_OPENED);
            }
        },
        onPackageSlotOpened(pkg, data) {
            if (pkg.slot_id && slotStatusMapRef.current.has(pkg.slot_id)) {
                setSlotStatusMap((prevMap) => {
                    const newMap = new Map(prevMap);
                    newMap.set(pkg.slot_id, SensorLockerStatus.OPENED);
                    return newMap;
                });
                slotStatusMapRef.current.set(pkg.slot_id, SensorLockerStatus.OPENED);
            }
        },
        onPackageSlotClosed(pkg, data) {
            if (pkg.slot_id && slotStatusMapRef.current.has(pkg.slot_id)) {
                setSlotStatusMap((prevMap) => {
                    const newMap = new Map(prevMap);
                    newMap.set(pkg.slot_id, SensorLockerStatus.CLOSED);
                    return newMap;
                });
                slotStatusMapRef.current.set(pkg.slot_id, SensorLockerStatus.CLOSED);
            }
        }
    });

    // Check if all slots are closed
    const allSlotsClosed = useMemo(() => {
        if (!slotStatusMap) return false;
        if (slotStatusMap.size === 0) return false;
        return Array.from(slotStatusMap.values()).every((status) => status === SensorLockerStatus.CLOSED);
    }, [slotStatusMap]);
    useEffect(() => {
        props.allClosedCallback?.(allSlotsClosed);
    }, [allSlotsClosed, props]);

    // Sort slots by status priority
    const { sortedSlots, openSlots, errorSlots, closedSlots, otherSlots } = useMemo(() => {
        if (!slots || !slotStatusMap)
            return {
                sortedSlots: [],
                openSlots: [],
                errorSlots: [],
                closedSlots: [],
                otherSlots: []
            };

        const openSlots: Slot[] = [];
        const errorSlots: Slot[] = [];
        const closedSlots: Slot[] = [];
        const otherSlots: Slot[] = [];

        const getStatusPriority = (status: SensorLockerStatus | undefined): number => {
            switch (status) {
                case SensorLockerStatus.OPENED:
                case SensorLockerStatus.OPENING:
                    return 0;
                case SensorLockerStatus.NOT_OPENED:
                case SensorLockerStatus.ERROR:
                    return 1;
                case SensorLockerStatus.CLOSED:
                    return 2;
                default:
                    return 3;
            }
        };

        const sortedSlots = slots.slice().sort((a, b) => {
            const priorityA = getStatusPriority(slotStatusMap.get(a.id));
            const priorityB = getStatusPriority(slotStatusMap.get(b.id));
            return priorityA - priorityB;
        });

        sortedSlots.forEach((slot) => {
            const status = slotStatusMap.get(slot.id);
            switch (status) {
                case SensorLockerStatus.OPENED:
                case SensorLockerStatus.OPENING:
                    openSlots.push(slot);
                    break;
                case SensorLockerStatus.NOT_OPENED:
                case SensorLockerStatus.ERROR:
                    errorSlots.push(slot);
                    break;
                case SensorLockerStatus.CLOSED:
                    closedSlots.push(slot);
                    break;
                default:
                    otherSlots.push(slot);
                    break;
            }
        });

        return { sortedSlots, openSlots, errorSlots, closedSlots, otherSlots };
    }, [slots, slotStatusMap]);

    useEffect(() => {
        props.isLoadingCallback?.(slotStatusMap === null || slots === null);
    }, [slotStatusMap, slots, props]);

    return {
        slots: sortedSlots,
        openSlots,
        errorSlots,
        closedSlots,
        otherSlots,
        slotStatusMap,
        allSlotsClosed,
        showClosedSlots,
        toggleShowClosedSlots
    };
};

export default useProcessOpenCloseSlots;
